import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FocusHeader } from '../components/FocusHeader';
import { StepIndicator } from '../components/StepIndicator';
import { useSession } from '../contexts/SessionContext';
import { supabase } from '../lib/supabase';
import { getWizardSteps, useWizardNavigation, nextIncompleteRoute } from '../lib/wizardSteps';
import { saveAndNavigate } from '../lib/navigationHelpers';
import { WIZARD_EXIT_ROUTE, WIZARD_PATHS, backUrlFor, sessionIdFromLocation, wizardUrl } from '../lib/wizardRoutes';
import { PenLine, Sparkles } from 'lucide-react';

export function SelectIntro() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, refreshSession } = useSession();
  const sessionId = sessionIdFromLocation(location);

  const [showCustom, setShowCustom] = useState(false);
  const [customIntro, setCustomIntro] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboardSession, setDashboardSession] = useState<any>(null);

  useEffect(() => {
    if (sessionId) {
      loadDashboardSession(sessionId);
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  const loadDashboardSession = async (sid: string) => {
    try {
      setLoading(true);

      const { data: sessionData, error: sessionError } = await supabase
        .from('sessions')
        .select('*')
        .eq('id', sid)
        .maybeSingle();

      if (sessionError || !sessionData) {
        console.error('Error loading session:', sessionError);
        setLoading(false);
        return;
      }

      setDashboardSession(sessionData);
      setLoading(false);
    } catch (err) {
      console.error('Error loading dashboard session:', err);
      setLoading(false);
    }
  };

  const activeSession = dashboardSession || session;
  const { handleStepClick } = useWizardNavigation(activeSession);
  const intros: string[] = Array.isArray(activeSession?.intros) ? activeSession.intros : [];
  const steps = getWizardSteps(activeSession, 5);

  const goTo = (path: string) => navigate(wizardUrl(path, activeSession?.id));
  const goBack = () => {
    const back = backUrlFor(WIZARD_PATHS.selectIntro, activeSession?.id);
    if (back) navigate(back);
  };
  const header = (
    <FocusHeader
      title="Intro kiezen"
      onBack={goBack}
      onClose={() => navigate(WIZARD_EXIT_ROUTE)}
      hideSaveStatus
    />
  );

  // Sla de intro op en ga naar de eerste stap die nog een keuze mist.
  // Afbeeldingen hangen aan het idee, niet aan de intro, en blijven staan.
  const chooseIntro = async (intro: string) => {
    const clean = intro.trim();
    if (!clean || saving || !activeSession?.id) return;

    if (clean === activeSession.selected_intro) {
      goTo(nextIncompleteRoute(activeSession));
      return;
    }

    try {
      setSaving(true);
      const next = { ...activeSession, selected_intro: clean };

      await saveAndNavigate(
        activeSession.id,
        { selected_intro: clean },
        ['selected_intro'],
        navigate,
        wizardUrl(nextIncompleteRoute(next), activeSession.id),
      );

      if (refreshSession) {
        await refreshSession();
      }
    } catch (error) {
      console.error('Error saving intro:', error);
      alert('Er ging iets mis bij het opslaan. Probeer het opnieuw.');
      setSaving(false);
    }
  };

  const handleCustomIntro = () => {
    if (!customIntro.trim()) {
      alert('Voer eerst een intro in.');
      return;
    }
    void chooseIntro(customIntro);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        {header}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-center min-h-[50vh]">
            <div className="text-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E94E87] mx-auto"></div>
              <p className="text-gray-600">Laden...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!activeSession) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        {header}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="card bg-red-50 border-red-200">
            <p className="text-red-800">Geen sessie gevonden. Open de blog opnieuw vanuit je concepten.</p>
            <button onClick={() => navigate(WIZARD_EXIT_ROUTE)} className="btn-secondary mt-4">
              Naar Mijn Blogs
            </button>
          </div>
        </main>
      </div>
    );
  }

  if (intros.length === 0) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        {header}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <StepIndicator steps={steps} onStepClick={handleStepClick} />
          <div className="card mt-6 space-y-4">
            <p className="text-gray-700 text-center">Er zijn nog geen intro's voor deze titel.</p>
            {!showCustom ? (
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => goTo(WIZARD_PATHS.introGenerate)}
                  className="btn-primary inline-flex items-center justify-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  Intro's laten schrijven
                </button>
                <button
                  onClick={() => setShowCustom(true)}
                  className="btn-secondary inline-flex items-center justify-center gap-2"
                >
                  <PenLine className="w-4 h-4" />
                  Schrijf je eigen intro
                </button>
              </div>
            ) : (
              <div className="card bg-gray-50">
                <h3 className="font-semibold text-[#231F20] mb-3">Schrijf je eigen intro</h3>
                <textarea
                  value={customIntro}
                  onChange={(e) => setCustomIntro(e.target.value)}
                  placeholder="Typ hier je eigen introductie..."
                  className="input-field w-full min-h-[120px] resize-y"
                  rows={5}
                  autoFocus
                />
                <div className="flex gap-3 mt-4">
                  <button
                    onClick={() => {
                      setShowCustom(false);
                      setCustomIntro('');
                    }}
                    className="btn-secondary flex-1"
                  >
                    Annuleren
                  </button>
                  <button
                    onClick={handleCustomIntro}
                    disabled={saving}
                    className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Gebruik deze intro
                  </button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F2EF]">
      {header}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <StepIndicator steps={steps} onStepClick={handleStepClick} />
        <div className="space-y-6 mt-6">
          <div className="space-y-2">
            <h1 className="text-2xl md:text-3xl font-bold text-[#231F20]">
              Kies je favoriete intro
            </h1>
            <p className="text-gray-600">Deze komt bovenaan je blog</p>
          </div>

          <div className="space-y-3">
            {intros.map((intro: string, index: number) => {
              const isChosen = intro === activeSession.selected_intro;
              return (
                <button
                  key={index}
                  onClick={() => void chooseIntro(intro)}
                  disabled={saving}
                  aria-pressed={isChosen}
                  className={`card text-left hover:border-[#231F20] hover:shadow-lg transition-all p-6 w-full disabled:opacity-50 disabled:cursor-not-allowed ${
                    isChosen ? 'border-[#231F20] ring-2 ring-[#231F20]/15' : ''
                  }`}
                >
                  <p className="text-gray-700 leading-relaxed">
                    {intro}
                  </p>
                  {isChosen && (
                    <p className="text-xs text-green-600 font-medium mt-2">Gekozen</p>
                  )}
                </button>
              );
            })}
          </div>

          {!showCustom ? (
            <button
              onClick={() => setShowCustom(true)}
              className="btn-secondary w-full flex items-center justify-center gap-2"
            >
              <PenLine className="w-4 h-4" />
              Of schrijf je eigen intro
            </button>
          ) : (
            <div className="card bg-gray-50">
              <h3 className="font-semibold text-[#231F20] mb-3">Schrijf je eigen intro</h3>
              <textarea
                value={customIntro}
                onChange={(e) => setCustomIntro(e.target.value)}
                placeholder="Typ hier je eigen introductie..."
                className="input-field w-full min-h-[120px] resize-y"
                rows={5}
              />
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => {
                    setShowCustom(false);
                    setCustomIntro('');
                  }}
                  className="btn-secondary flex-1"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleCustomIntro}
                  disabled={saving}
                  className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Gebruik deze intro
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
